import { PlusOutlined } from '@ant-design/icons';
import { Button, Input, InputRef, Space, Tag } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { alertMessage } from '../../utils/alertMessage';
import { useTranslation } from 'react-i18next';

type TagInputProps = {
  tags: string[];
  setTags: React.Dispatch<React.SetStateAction<string[]>>;
  inputRegex?: RegExp;
  errorMessage?: string; // Optional custom error message
};

const TagInput = ({ tags, setTags, inputRegex, errorMessage }: TagInputProps) => {
  const { t } = useTranslation();
  const [editInputIndex, setEditInputIndex] = useState(-1);
  const [editInputValue, setEditInputValue] = useState('');
  const [inputVisible, setInputVisible] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<InputRef>(null);
  const editInputRef = useRef<InputRef>(null);

  useEffect(() => {
    if (inputVisible) {
      inputRef.current?.focus();
    }
  }, [inputVisible]);

  useEffect(() => {
    editInputRef.current?.focus();
  }, [editInputValue]);

  const handleClose = (removedTag: string) => {
    const newTags = tags.filter((tag) => tag !== removedTag);
    setTags(newTags);
  };

  const isPassRegex = (value: string) => {
    if (inputRegex) {
      return inputRegex.test(value);
    }
    return true;
  };

  const handleConfirm = (value: string, index?: number) => {
    if (value && !tags.includes(value)) {
      if (isPassRegex(value)) {
        const newTags = [...tags];
        if (index !== undefined) {
          newTags[index] = value; // Edit existing tag
        } else {
          newTags.push(value); // Add new tag
        }
        setTags(newTags);
      } else {
        alertMessage('error', errorMessage || t('general.inputError.empty'));
        return;
      }
    }
    setInputVisible(false);
    setEditInputIndex(-1);
    setInputValue('');
    setEditInputValue('');
  };

  return (
    <Space wrap>
      {tags.map((tag, index) => {
        // Edit tag value
        if (editInputIndex === index) {
          return (
            <Input
              ref={editInputRef}
              key={tag}
              value={editInputValue}
              onChange={(e) => {
                setEditInputValue(e.target.value);
              }}
              onBlur={() => handleConfirm(editInputValue, editInputIndex)}
              onPressEnter={() => handleConfirm(editInputValue, editInputIndex)}
            />
          );
        }

        return (
          <Tag key={tag} closable onClose={() => handleClose(tag)}>
            <span
              onDoubleClick={(e) => {
                setEditInputIndex(index);
                setEditInputValue(tag);
                e.preventDefault();
              }}
            >
              {tag}
            </span>
          </Tag>
        );
      })}

      {inputVisible ? (
        <Input
          ref={inputRef}
          type="text"
          allowClear
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
          }}
          onBlur={() => handleConfirm(inputValue)}
          onPressEnter={() => handleConfirm(inputValue)}
        />
      ) : (
        <Button
          size="small"
          onClick={() => {
            setInputVisible(true);
          }}
          shape="circle"
          icon={<PlusOutlined />}
        />
      )}
    </Space>
  );
};

export default TagInput;
